import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { MapPin, X, Sparkles, ZapOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { motion, AnimatePresence } from 'framer-motion'

const LocationPermissionDialog = ({ isOpen, onOpenChange, onAccept, onDecline, triggerWord }) => {
  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {isOpen && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[200] bg-background/40 backdrop-blur-md"
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild>
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="fixed left-[50%] top-[50%] z-[201] w-[calc(100%-2rem)] max-w-[340px] translate-x-[-50%] translate-y-[-50%] gap-4 border border-border/40 bg-background/95 p-6 shadow-2xl duration-200 rounded-[2rem] outline-none"
              >
                <div className="flex flex-col items-center text-center space-y-5">
                  {/* Visual Header */}
                  <div className="relative">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary rotate-3">
                      <MapPin className="h-8 w-8" />
                    </div>
                    <div className="absolute -right-2 -top-2 bg-background border border-border/20 shadow-sm rounded-full p-1.5 text-amber-500 animate-pulse">
                      <Sparkles className="h-4 w-4 fill-current" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-2">
                    <DialogPrimitive.Title className="text-xl font-black tracking-tight text-foreground">
                      Enable Smart Location?
                    </DialogPrimitive.Title>
                    <div className="flex flex-col items-center gap-2">
                      <p className="text-muted-foreground text-sm font-medium">
                        You typed{' '}
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-foreground font-bold border border-border/10">
                          "{triggerWord || 'nearest'}"
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Skimmable Benefits/Losses */}
                  <div className="grid w-full gap-3 py-1">
                    <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-primary/5 border border-primary/10">
                      <Sparkles className="h-4 w-4 text-primary shrink-0" />
                      <span className="text-[12px] font-bold text-foreground text-left leading-tight">
                        Get smarter suggestions based on your needs
                      </span>
                    </div>
                    <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl opacity-80">
                      <ZapOff className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-[12px] font-normal text-muted-foreground text-left leading-tight">
                        Declining disables "nearest" and local AI features
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col w-full gap-2 pt-2">
                    <Button
                      onClick={onAccept}
                      className="w-full h-12 rounded-2xl font-black text-sm bg-primary hover:bg-primary/90 shadow-xl shadow-primary/20 transition-all active:scale-[0.96]"
                    >
                      Allow Location Access
                    </Button>
                    <DialogPrimitive.Close asChild>
                      <Button
                        variant="ghost"
                        onClick={onDecline}
                        className="w-full h-10 rounded-xl font-bold text-[13px] text-muted-foreground/60 hover:text-muted-foreground hover:bg-transparent transition-all"
                      >
                        Maybe Later
                      </Button>
                    </DialogPrimitive.Close>
                  </div>
                </div>

                {/* Subtle Close */}
                <DialogPrimitive.Close className="absolute right-5 top-5 rounded-full p-1.5 opacity-40 transition-opacity hover:opacity-100 hover:bg-muted outline-none">
                  <X className="h-4 w-4" />
                </DialogPrimitive.Close>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  )
}

export { LocationPermissionDialog }
